import clsx from "clsx";

import {
  StepMenu,
  StepMenuItem,
} from "@/components/Interface/Stepper/StepMenu";

/** The design writes single-digit steps as "03 / 04", not "3 / 4". */
const formatStepNumber = (value: number) => String(value).padStart(2, "0");

interface StepFlowStepperProps {
  step: number;
  totalSteps: number;
  stepLabel: string;
  description?: string;
  /** Every step of the flow, in order - powers the jump menu behind the chevron. */
  menuItems: StepMenuItem[];
  /** Accessible name of the chevron toggle button. */
  menuToggleLabel: string;
}

/**
 * Progress indicator of a multi-step flow: one block per step, the current
 * step's name, the "03 / 04" counter and a menu to jump to any other step.
 */
export const StepFlowStepper = ({
  step,
  totalSteps,
  stepLabel,
  description,
  menuItems,
  menuToggleLabel,
}: StepFlowStepperProps) => (
  <div className="flex w-full flex-col">
    {/* DireProgressBar is a continuous 0-100 fill and collapses its track at
        value=100, which would hide every completed step of a discrete
        indicator - so one block per step, with its fill/track tokens. */}
    <div className="flex w-full gap-1" aria-hidden="true">
      {Array.from({ length: totalSteps }, (_, index) => (
        <span
          key={index}
          className={clsx(
            "h-1 flex-1 rounded-full",
            index < step ? "bg-surface-primary" : "bg-surface-secondary",
          )}
        />
      ))}
    </div>

    <div className="flex h-8 w-full items-center justify-between gap-4">
      <p className="text-body-base font-semibold leading-none text-foreground-primary">
        {stepLabel}
      </p>
      <StepMenu
        items={menuItems}
        currentIndex={step - 1}
        toggleLabel={menuToggleLabel}
        counterLabel={`${formatStepNumber(step)} / ${formatStepNumber(totalSteps)}`}
        variant="stepper"
      />
    </div>

    {description && (
      <p className="text-body-sm leading-none text-foreground-primary">
        {description}
      </p>
    )}
  </div>
);
